/**
 * IHE XDS.b / XCA Configuration Manager
 * Admin page for configuring IHE document sharing profiles, browsing the registry, and viewing audit logs.
 */

import React, { useState, useEffect } from 'react';
import { Share2, RefreshCw, CheckCircle, AlertCircle, FileText, Search } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { apiClient } from '@/services/apiClient';

interface IHEConfig {
  home_community_id: string;
  repository_unique_id: string;
  assigning_authority_oid: string;
  lanes_registry_url: string;
  lanes_repository_url: string;
  lanes_xca_query_url: string;
  lanes_xca_retrieve_url: string;
  ws_security_enabled: boolean;
  xml_signature_enabled: boolean;
}

interface RegistryDocument {
  id: string;
  document_unique_id: string;
  patient_id: string;
  title: string;
  type_code: string;
  created_at: string;
  status: string;
  size: number;
}

interface AuditEntry {
  id: number;
  timestamp: string;
  event_type: string;
  event_action: string;
  event_outcome: string;
  patient_id: string;
  document_id: string;
  source: string;
}

export function IHEConfigManager() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Community identity
  const [homeCommunityId, setHomeCommunityId] = useState('');
  const [repositoryUniqueId, setRepositoryUniqueId] = useState('');
  const [assigningAuthorityOid, setAssigningAuthorityOid] = useState('');

  // LANES endpoints
  const [lanesRegistryUrl, setLanesRegistryUrl] = useState('');
  const [lanesRepositoryUrl, setLanesRepositoryUrl] = useState('');
  const [lanesXcaQueryUrl, setLanesXcaQueryUrl] = useState('');
  const [lanesXcaRetrieveUrl, setLanesXcaRetrieveUrl] = useState('');

  // Security
  const [wsSecurityEnabled, setWsSecurityEnabled] = useState(false);
  const [xmlSignatureEnabled, setXmlSignatureEnabled] = useState(false);

  // Document registry browser
  const [documents, setDocuments] = useState<RegistryDocument[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [patientIdFilter, setPatientIdFilter] = useState('');

  // Register CCD
  const [registerMemberId, setRegisterMemberId] = useState('');
  const [registerTitle, setRegisterTitle] = useState('');
  const [registering, setRegistering] = useState(false);
  const [registerResult, setRegisterResult] = useState<{ status: string; document_unique_id?: string; message?: string } | null>(null);

  // Audit log
  const [auditEntries, setAuditEntries] = useState<AuditEntry[]>([]);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [eventTypeFilter, setEventTypeFilter] = useState('');

  useEffect(() => {
    fetchConfig();
    fetchDocuments();
    fetchAuditLog();
  }, []);

  const fetchConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await apiClient.get('/api/ihe/config');
      const data = resp.data;
      setHomeCommunityId(data.home_community_id || '');
      setRepositoryUniqueId(data.repository_unique_id || '');
      setAssigningAuthorityOid(data.assigning_authority_oid || '');
      setLanesRegistryUrl(data.lanes_registry_url || '');
      setLanesRepositoryUrl(data.lanes_repository_url || '');
      setLanesXcaQueryUrl(data.lanes_xca_query_url || '');
      setLanesXcaRetrieveUrl(data.lanes_xca_retrieve_url || '');
      setWsSecurityEnabled(data.ws_security_enabled || false);
      setXmlSignatureEnabled(data.xml_signature_enabled || false);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load IHE configuration');
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    setError(null);
    try {
      await apiClient.put('/api/ihe/config', {
        home_community_id: homeCommunityId,
        repository_unique_id: repositoryUniqueId,
        assigning_authority_oid: assigningAuthorityOid,
        lanes_registry_url: lanesRegistryUrl,
        lanes_repository_url: lanesRepositoryUrl,
        lanes_xca_query_url: lanesXcaQueryUrl,
        lanes_xca_retrieve_url: lanesXcaRetrieveUrl,
        ws_security_enabled: wsSecurityEnabled,
        xml_signature_enabled: xmlSignatureEnabled,
      });
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const fetchDocuments = async (patientId?: string) => {
    setLoadingDocuments(true);
    try {
      const params = patientId ? { patient_id: patientId } : {};
      const resp = await apiClient.get('/api/ihe/registry/documents', { params });
      setDocuments(resp.data.documents || resp.data || []);
    } catch {
      // silent
    } finally {
      setLoadingDocuments(false);
    }
  };

  const registerCcd = async () => {
    setRegistering(true);
    setRegisterResult(null);
    try {
      const resp = await apiClient.post('/api/ihe/registry/register', {
        member_id: registerMemberId,
        title: registerTitle,
      });
      setRegisterResult(resp.data);
      fetchDocuments();
    } catch (err: any) {
      setRegisterResult({ status: 'error', message: err?.response?.data?.error || 'Failed to register CCD' });
    } finally {
      setRegistering(false);
    }
  };

  const fetchAuditLog = async (eventType?: string) => {
    setLoadingAudit(true);
    try {
      const params = eventType ? { event_type: eventType } : {};
      const resp = await apiClient.get('/api/ihe/audit-log', { params });
      setAuditEntries(resp.data.entries || resp.data || []);
    } catch {
      // silent
    } finally {
      setLoadingAudit(false);
    }
  };

  const handleDocumentSearch = () => {
    fetchDocuments(patientIdFilter || undefined);
  };

  const handleAuditFilter = () => {
    fetchAuditLog(eventTypeFilter || undefined);
  };

  const formatBytes = (bytes: number): string => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading IHE configuration...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Share2 className="h-7 w-7 text-blue-600" />
        <div>
          <h2 className="text-2xl font-bold">IHE XDS.b / XCA</h2>
          <p className="text-sm text-muted-foreground">
            Configure IHE document sharing profiles, manage the registry, and view audit logs
          </p>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-md bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      {/* Community Identity */}
      <Card>
        <CardHeader>
          <CardTitle>Community Identity</CardTitle>
          <CardDescription>OIDs and identifiers for this community within the IHE affinity domain</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="home_community_id">Home Community ID</Label>
              <Input
                id="home_community_id"
                value={homeCommunityId}
                onChange={(e) => setHomeCommunityId(e.target.value)}
                placeholder="urn:oid:2.16.840.1.113883..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="repository_unique_id">Repository Unique ID</Label>
              <Input
                id="repository_unique_id"
                value={repositoryUniqueId}
                onChange={(e) => setRepositoryUniqueId(e.target.value)}
                placeholder="2.16.840.1.113883..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="assigning_authority_oid">Assigning Authority OID</Label>
              <Input
                id="assigning_authority_oid"
                value={assigningAuthorityOid}
                onChange={(e) => setAssigningAuthorityOid(e.target.value)}
                placeholder="2.16.840.1.113883..."
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* LANES Endpoints */}
      <Card>
        <CardHeader>
          <CardTitle>LANES Endpoints</CardTitle>
          <CardDescription>XDS.b and XCA service endpoint URLs for the LANES network</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="lanes_registry_url">Registry URL</Label>
              <Input
                id="lanes_registry_url"
                value={lanesRegistryUrl}
                onChange={(e) => setLanesRegistryUrl(e.target.value)}
                placeholder="https://lanes.example.org/registry"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lanes_repository_url">Repository URL</Label>
              <Input
                id="lanes_repository_url"
                value={lanesRepositoryUrl}
                onChange={(e) => setLanesRepositoryUrl(e.target.value)}
                placeholder="https://lanes.example.org/repository"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lanes_xca_query_url">XCA Query URL</Label>
              <Input
                id="lanes_xca_query_url"
                value={lanesXcaQueryUrl}
                onChange={(e) => setLanesXcaQueryUrl(e.target.value)}
                placeholder="https://lanes.example.org/xca/query"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lanes_xca_retrieve_url">XCA Retrieve URL</Label>
              <Input
                id="lanes_xca_retrieve_url"
                value={lanesXcaRetrieveUrl}
                onChange={(e) => setLanesXcaRetrieveUrl(e.target.value)}
                placeholder="https://lanes.example.org/xca/retrieve"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Security Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Security Settings</CardTitle>
          <CardDescription>WS-Security and XML signature settings for SOAP transactions</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-6">
            <div className="flex items-center gap-3">
              <Switch
                checked={wsSecurityEnabled}
                onCheckedChange={setWsSecurityEnabled}
                id="ws_security_enabled"
              />
              <Label htmlFor="ws_security_enabled">WS-Security Enabled</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={xmlSignatureEnabled}
                onCheckedChange={setXmlSignatureEnabled}
                id="xml_signature_enabled"
              />
              <Label htmlFor="xml_signature_enabled">XML Signature Enabled</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save */}
      <div className="flex items-center gap-3">
        <Button onClick={saveConfig} disabled={saving}>
          {saving ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : null}
          Save Configuration
        </Button>
      </div>

      {/* Document Registry Browser */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Document Registry Browser</CardTitle>
            <CardDescription>Browse documents registered in the XDS.b registry</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Input
              className="w-48"
              placeholder="Filter by Patient ID"
              value={patientIdFilter}
              onChange={(e) => setPatientIdFilter(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleDocumentSearch()}
            />
            <Button variant="outline" size="sm" onClick={handleDocumentSearch} disabled={loadingDocuments}>
              <Search className="mr-2 h-4 w-4" />
              Search
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Document ID</TableHead>
                  <TableHead>Patient</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Size</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      {loadingDocuments ? (
                        <span className="flex items-center justify-center gap-2">
                          <RefreshCw className="h-4 w-4 animate-spin" /> Loading documents...
                        </span>
                      ) : (
                        'No documents found'
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  documents.map((doc) => (
                    <TableRow key={doc.id}>
                      <TableCell className="font-mono text-xs max-w-[200px] truncate" title={doc.document_unique_id}>
                        {doc.document_unique_id}
                      </TableCell>
                      <TableCell>{doc.patient_id}</TableCell>
                      <TableCell>{doc.title}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{doc.type_code}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(doc.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Badge variant={doc.status === 'Approved' ? 'default' : 'secondary'}>
                          {doc.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{formatBytes(doc.size)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Register CCD */}
      <Card>
        <CardHeader>
          <CardTitle>Register CCD</CardTitle>
          <CardDescription>Generate a CCD document and register it in the XDS.b registry</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="register_member_id">Member ID</Label>
              <Input
                id="register_member_id"
                value={registerMemberId}
                onChange={(e) => setRegisterMemberId(e.target.value)}
                placeholder="Enter member ID"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="register_title">Document Title</Label>
              <Input
                id="register_title"
                value={registerTitle}
                onChange={(e) => setRegisterTitle(e.target.value)}
                placeholder="Continuity of Care Document"
              />
            </div>
            <div className="flex items-end">
              <Button onClick={registerCcd} disabled={registering || !registerMemberId}>
                {registering ? (
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <FileText className="mr-2 h-4 w-4" />
                )}
                Generate & Register CCD
              </Button>
            </div>
          </div>
          {registerResult && (
            <div
              className={`rounded-md p-3 text-sm ${
                registerResult.status === 'ok' || registerResult.status === 'success'
                  ? 'bg-green-50 text-green-700'
                  : 'bg-red-50 text-red-700'
              }`}
            >
              <div className="flex items-center gap-2">
                {registerResult.status === 'ok' || registerResult.status === 'success' ? (
                  <CheckCircle className="h-4 w-4" />
                ) : (
                  <AlertCircle className="h-4 w-4" />
                )}
                {registerResult.message || 'CCD registered successfully'}
              </div>
              {registerResult.document_unique_id && (
                <div className="mt-1 font-mono text-xs">
                  Document ID: {registerResult.document_unique_id}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Audit Log */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Audit Log</CardTitle>
            <CardDescription>IHE ATNA audit trail for document transactions</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Input
              className="w-48"
              placeholder="Filter by event type"
              value={eventTypeFilter}
              onChange={(e) => setEventTypeFilter(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAuditFilter()}
            />
            <Button variant="outline" size="sm" onClick={handleAuditFilter} disabled={loadingAudit}>
              <Search className="mr-2 h-4 w-4" />
              Filter
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Outcome</TableHead>
                  <TableHead>Patient</TableHead>
                  <TableHead>Document</TableHead>
                  <TableHead>Source</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {auditEntries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      {loadingAudit ? (
                        <span className="flex items-center justify-center gap-2">
                          <RefreshCw className="h-4 w-4 animate-spin" /> Loading audit log...
                        </span>
                      ) : (
                        'No audit entries found'
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  auditEntries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {new Date(entry.timestamp).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{entry.event_type}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">{entry.event_action}</TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            entry.event_outcome === 'Success' || entry.event_outcome === '0'
                              ? 'default'
                              : 'destructive'
                          }
                        >
                          {entry.event_outcome}
                        </Badge>
                      </TableCell>
                      <TableCell>{entry.patient_id}</TableCell>
                      <TableCell className="font-mono text-xs max-w-[160px] truncate" title={entry.document_id}>
                        {entry.document_id}
                      </TableCell>
                      <TableCell className="text-sm">{entry.source}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
